import { Controller, ControllerProps, FieldValues } from "react-hook-form";
import { Text, TextInput } from "react-native";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlHelper,
  FormControlHelperText,
} from "../../gluestack/form-control";
import React, { useEffect, useRef } from "react";
import clsx from "clsx";
import { tva } from "@gluestack-ui/nativewind-utils/tva";
import { VariantProps } from "@gluestack-ui/nativewind-utils";
import { AlertCircleIcon } from "ui/gluestack/icon";
import { requiredValidation } from "ui/utils/Validators";

export interface Props<TFieldValues extends FieldValues = FieldValues> {
  id: string;
  name: ControllerProps<TFieldValues>["name"];
  control: ControllerProps<TFieldValues>["control"];
  isEditing?: boolean;
  isInvalid?: boolean;
  onSubmitEditing?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  fallbackText?: string;
  autoFocus?: boolean;
  className?: string;
  rules?: ControllerProps<TFieldValues>["rules"];
  invalidMessage?: string;
  helperText?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
}

const textInputVariantStyles = tva({
  base: "",
  variants: {
    variant: {
      default: "outline outline-primary-300 rounded-sm",
      large:
        "text-lg font-semibold pb-0.5 border-0 rounded-0 border-b outline-0 outline-b border-primary-300 placeholder:text-typography-500",
    },
  },
});

const textDisplayVariantStyles = tva({
  base: "",
  variants: {
    variant: {
      default: "",
      large: "text-lg pb-0.5 font-semibold border-b border-transparent",
    },
  },
});

export function FormControlledEditableText<
  TFieldValues extends FieldValues = FieldValues,
>({
  id,
  name,
  control,
  isInvalid,
  isEditing,
  onSubmitEditing,
  onBlur,
  placeholder,
  fallbackText,
  autoFocus,
  className,
  rules,
  variant,
  invalidMessage,
  helperText,
  isRequired,
  isDisabled,
}: Props<TFieldValues> & VariantProps<typeof textInputVariantStyles>) {
  const localRules = {
    required: isRequired
      ? requiredValidation(fallbackText ?? "Field")
      : undefined,
    ...rules,
  };

  const inputRef = useRef<React.ComponentRef<typeof TextInput>>(null);

  useEffect(() => {
    if (isEditing && inputRef.current && autoFocus) {
      inputRef.current.focus();
    }
  }, [isEditing, autoFocus]);

  return (
    <Controller
      control={control}
      name={name}
      rules={localRules}
      render={({ fieldState: { error }, field: { value, onChange } }) => (
        <FormControl isInvalid={isInvalid || !!error} className={className}>
          {isEditing ? (
            <TextInput
              id={id}
              ref={inputRef}
              className={textInputVariantStyles({ variant })}
              placeholder={placeholder}
              value={value}
              onChangeText={onChange}
              onSubmitEditing={onSubmitEditing}
              onBlur={onBlur}
              readOnly={isDisabled}
            />
          ) : (
            <Text
              className={clsx(
                value ? "text-typography-800" : "text-typography-500",
                textDisplayVariantStyles({ variant }),
              )}
            >
              {value || fallbackText}
            </Text>
          )}

          {helperText ? (
            <FormControlHelper>
              <FormControlHelperText className="text-gray-500">
                {helperText ?? " "}
              </FormControlHelperText>
            </FormControlHelper>
          ) : null}
          {invalidMessage || error?.message ? (
            <FormControlError>
              <FormControlErrorIcon
                as={AlertCircleIcon}
                className="text-error-500"
                size="sm"
              />
              <FormControlErrorText className="text-error-500 text-sm">
                {error?.message || invalidMessage}
              </FormControlErrorText>
            </FormControlError>
          ) : null}
        </FormControl>
      )}
    />
  );
}
